import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import type { MessageKey } from '@/i18n'
import { Btn, Cta, Footer, JobTile, Row, Strip, V1Screen } from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'
import { cropWord, shortLot, tr } from '@/features/gate/v1/text'
import type { ProcessState } from '@/features/process/projection'
import type { PackLot } from '@/offline/sitePack'
import { cartonSplit, lotState, pouchesLeft, type LotState } from '../model/reconcile'
import { dateBoxes } from './model'
import { Lead, Note, PackHeader, WithHall } from './ui'
import { ki, packSize } from './words'
import { useHall } from './useHall'

/** Held first, then recalled, waiting, packing, released, withdrawn (as the full screen). */
const ORDER: LotState[] = ['HELD', 'RECALLED', 'WAITING', 'PACKING', 'RELEASED', 'WITHDRAWN']

const HOLD_REASONS = ['LAB_FAIL', 'RUN_MIXED', 'LABEL_MISMATCH', 'QUALITY']

/** The pill and the row edge of each state. */
const TONE: Record<LotState, { bg: string; fg: string; edge: string }> = {
  PACKING: { bg: C.helpWash, fg: C.help, edge: C.rule },
  WAITING: { bg: C.sunk, fg: C.muted, edge: C.rule },
  RELEASED: { bg: C.okWash, fg: C.ok, edge: C.rule },
  HELD: { bg: C.warnWash, fg: C.accDark, edge: C.acc },
  RECALLED: { bg: C.badWash, fg: C.bad, edge: C.bad },
  WITHDRAWN: { bg: C.badWash, fg: C.bad, edge: C.rule },
}

const num = (n: number) => n.toLocaleString('en-IN')
const day = (iso: string) => dateBoxes(iso).join(' ')

/** "मूंगफली · 500 ग्राम" under a lot. */
function lotLine(l: PackLot, skuName: string | undefined): string {
  return [cropWord(l.sku_name ?? skuName), l.net_g ? packSize(l.net_g) : null]
    .filter(Boolean)
    .join(' · ')
}

function reasonText(reason: string | null | undefined): string | null {
  return reason && HOLD_REASONS.includes(reason)
    ? tr('pack.v1.lots.list.reason', {
        reason: tr(`pack.v1.lots.list.reason.${reason}` as MessageKey),
      })
    : null
}

/**
 * V1 — पैक हुए लॉट: every pack lot at the hall and what it waits for (F7.6 / F7-B1.a from the
 * floor). Same rules as the full PackLotsPage: the pouches stay in the waiting area until the
 * office releases the lot; held, recalled or withdrawn lots never leave. The hall keeper (and the
 * supervisor) go on to dispatch from here when released pouches are left.
 */
export function V1PackLots() {
  return <WithHall>{(state) => <Lots state={state} />}</WithHall>
}

function Lots({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  const actor = useActor()
  const hall = useHall(state)
  const lots = [...state.packLots.values()].sort(
    (a, b) =>
      ORDER.indexOf(lotState(a)) - ORDER.indexOf(lotState(b)) ||
      b.pack_date.localeCompare(a.pack_date),
  )
  const canDispatch = hasAnyRole(actor.roles, [ROLES.STORE_KEEPER, ROLES.SUPERVISOR])
  const operator = hasAnyRole(actor.roles, [ROLES.MACHINE_OPERATOR, ROLES.SUPERVISOR])
  const releasedLeft = lots.filter((l) => lotState(l) === 'RELEASED' && pouchesLeft(l) > 0)
  // The hall keeper came from the hall board; the line operator from the pack line's आज.
  const home = () => navigate(operator ? '/m/pack' : '/m/pack/hall')

  return (
    <V1Screen
      nav
      header={
        <PackHeader
          onBack={home}
          icon="pouch"
          title={tr('pack.v1.lots.list.title')}
          sub={state.pack.site_code ?? state.pack.site_name ?? undefined}
        />
      }
      footer={
        <Footer>
          {canDispatch && releasedLeft.length > 0 ? (
            <Cta
              label={tr('pack.v1.hall.dispatch')}
              icon="truck"
              onClick={() => navigate('/m/pack/dispatch')}
            />
          ) : (
            <Cta label={tr('pack.v1.home')} icon="home" lead onClick={home} />
          )}
        </Footer>
      }
    >
      {canDispatch && releasedLeft.length > 0 && (
        <Strip icon="truck">{tr('pack.v1.lots.list.ready', { n: releasedLeft.length })}</Strip>
      )}
      {lots.length === 0 && <Strip icon="hourglass">{tr('pack.v1.lots.list.none')}</Strip>}
      {lots.map((l) => (
        <LotCard key={l.id} state={state} lot={l} />
      ))}
      {hall.online && (
        <Row>
          <Btn icon="download" disabled={hall.busy} onClick={hall.refresh}>
            {tr('pack.v1.home.refresh')}
          </Btn>
        </Row>
      )}
    </V1Screen>
  )
}

/** One lot: its tail, product, state; the dates, pouches and cartons; what to do with it. */
function LotCard({ state, lot }: { state: ProcessState; lot: PackLot }) {
  const s = lotState(lot)
  const tone = TONE[s]
  const run = lot.run_id ? state.packRuns.get(lot.run_id) : undefined
  const perCarton = run?.sku.pouches_per_carton
  const split = lot.pouch_count && perCarton ? cartonSplit(lot.pouch_count, perCarton) : null
  const reason = s === 'HELD' ? reasonText(lot.hold_reason) : null
  return (
    <section
      style={{
        background: C.surface,
        border: `${tone.edge === C.rule ? 1.5 : 2}px solid ${tone.edge}`,
        borderRadius: 12,
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Lead icon="pouch" bg={tone.bg} color={tone.fg} />
        <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
          <b style={{ fontSize: 19, lineHeight: 1.2 }}>
            {tr('pack.v1.lotTail', { tail: shortLot(lot.code) })}
          </b>
          <span style={{ fontSize: 15, color: C.muted, lineHeight: 1.25 }}>
            {lotLine(lot, run?.sku.name)}
          </span>
        </span>
        <span
          style={{
            flexShrink: 0,
            padding: '3px 10px',
            borderRadius: 14,
            background: tone.bg,
            color: tone.fg,
            fontSize: 15,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          {tr(`pack.v1.lots.list.state.${s}` as MessageKey)}
        </span>
      </div>
      <span style={{ fontSize: 15, color: C.muted, lineHeight: 1.3 }}>
        {tr('pack.v1.lots.list.dates', { pkd: day(lot.pack_date), bb: day(lot.best_before) })}
      </span>
      {lot.pouch_count != null && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            columnGap: 12,
            rowGap: 2,
            fontSize: 17,
          }}
        >
          <b>{tr('pack.v1.pouches', { count: lot.pouch_count })}</b>
          {split && perCarton && (
            <span style={{ fontSize: 15, color: C.muted }}>
              {tr('pack.v1.lots.list.cartons', {
                full: split.full,
                per: perCarton,
                partial: split.partial,
              })}
            </span>
          )}
          {s === 'RELEASED' && (
            <b style={{ color: C.ok }}>
              {tr('pack.v1.lots.left', { count: num(pouchesLeft(lot)) })}
            </b>
          )}
        </div>
      )}
      {s === 'WAITING' && <Note icon="hourglass">{tr('pack.v1.lots.list.waitingHint')}</Note>}
      {s === 'HELD' && (
        <Note icon="bang" tone="warn">
          {reason
            ? `${tr('pack.v1.lots.list.heldHint')} ${reason}`
            : tr('pack.v1.lots.list.heldHint')}
        </Note>
      )}
      {s === 'RECALLED' && (
        <Note icon="bang" tone="warn">
          {tr('pack.v1.lots.list.recalledHint')}
        </Note>
      )}
    </section>
  )
}

/** पैक हुए लॉट: every lot at the hall and what it waits for (the full build's pack lots tile). */
export function LotsTile() {
  const navigate = useNavigate()
  return (
    <JobTile
      icon={ki('pouch')}
      color={C.acc}
      title={tr('pack.v1.lots.list.see')}
      line={tr('pack.v1.lots.list.seeLine')}
      onClick={() => navigate('/m/pack/lots')}
    />
  )
}

/**
 * रुके रन जाँचें: a supervisor at the hall reaches the held-run check (a pack run held for a
 * SURPLUS is corrected there) — the hall phone opens on the pack screens, not the process home.
 */
export function HeldRunsTile({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  const actor = useActor()
  const held = [...state.runs.values()].filter((r) => r.status === 'EXCEPTION').length
  if (!held || !hasAnyRole(actor.roles, [ROLES.SUPERVISOR])) return null
  return (
    <JobTile
      icon="bang"
      color={C.bad}
      title={tr('pack.v1.heldRuns.title')}
      line={tr('pack.v1.heldRuns.line', { count: held })}
      onClick={() => navigate('/m/process/investigate?from=pack')}
    />
  )
}

/**
 * पैकिंग हॉल: a supervisor on the pack line reaches the hall (pouches at the hall, dispatch) — the
 * phone home sends him to the pack line. Hidden for everyone else.
 */
export function HallTile() {
  const navigate = useNavigate()
  const actor = useActor()
  if (!hasAnyRole(actor.roles, [ROLES.SUPERVISOR])) return null
  return (
    <JobTile
      icon="home"
      color={C.ink}
      title={tr('pack.v1.hallTile.title')}
      line={tr('pack.v1.hallTile.line')}
      onClick={() => navigate('/m/pack/hall')}
    />
  )
}
